import { FeedbackDetailsConversation } from '@modules/feedback/components/FeedbackDetails/FeedbackDetailsConversation';
import {
  FeedbackSummary,
  FeedbackSummaryList,
} from '@modules/feedback/components/FeedbackSummary';
import { getFeedbackSummary } from '@modules/feedback/components/FeedbackSummary/utils';
import { SummaryOrFeedbackLabel } from '@modules/feedback/components/SummaryOrFeedbackLabel';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { useIsFeedbackOnFeedbackV2Enabled } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { Box, Paper, Stack, Tooltip, alpha } from '@mui/material';
import { Feedback, FeedbackLabel } from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';

interface FeedbackSummaryPreviewCardProps {
  feedback: Feedback;
  selectedLabels: FeedbackLabel[];
  openFeedbackDetailsDrawer: () => void;
  shouldShowSavedFeedbackCard?: boolean;
  feedbackListViewType: FeedbackListViewType;
}

export const FeedbackSummaryPreviewCard = ({
  feedback,
  selectedLabels,
  openFeedbackDetailsDrawer,
  shouldShowSavedFeedbackCard,
  feedbackListViewType,
}: FeedbackSummaryPreviewCardProps) => {
  const summary = getFeedbackSummary(feedback);
  const { isFeedbackOnFeedbackV2Enabled } = useIsFeedbackOnFeedbackV2Enabled();
  const {
    tenant: {
      dashboardSettings: { showUnTranslatedRecords = false },
    },
  } = useGetUser();
  const flaggedSummaryRecords = useFeedbackOnFeedbackStore(
    ({ flaggedSummaryRecords }) => flaggedSummaryRecords,
  );
  const flaggedRecord = flaggedSummaryRecords.find(
    (record) => record.id === feedback.id,
  );
  const isFlagged = flaggedRecord
    ? !!flaggedRecord.summaryFlagType
    : !!summary?.flag;

  const shouldShowFeedbackSummaryActions =
    feedbackListViewType !== FeedbackListViewType.SAMPLE;

  const feedbackSummaryPreview = (
    <>
      {isFlagged && (
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: alpha('rgb(0,0,0)', 0.25),
            zIndex: 3,
          }}
        />
      )}
      <Stack spacing={2}>
        <SummaryOrFeedbackLabel
          feedbackID={feedback.id}
          isSummaryView
          label="Summary"
          summary={summary}
          shouldShowFeedbackSummaryActions={shouldShowFeedbackSummaryActions}
        />
        <FeedbackSummaryList summaryUnits={summary?.units} />
      </Stack>
    </>
  );

  const shouldShowFeedbackSummary =
    isFeedbackOnFeedbackV2Enabled && !summary?.units?.length;

  return (
    <Tooltip
      title={shouldShowFeedbackSummaryActions ? 'Click to expand' : ''}
      placement="top"
      arrow
      PopperProps={{
        disablePortal: true,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          position: 'relative',
          maxHeight: '360px',
          overflow: 'hidden',
          p: shouldShowFeedbackSummary ? 1 : 2,
          pb: 0,
          bgcolor: 'feedback.summaryCard',
          borderColor: shouldShowSavedFeedbackCard
            ? (theme) => theme.palette.background.paper
            : 'transparent',
          outline: shouldShowSavedFeedbackCard ? '2px solid' : undefined,
          outlineColor: shouldShowSavedFeedbackCard
            ? (theme) => theme.palette.background.paper
            : undefined,
          borderRadius: 2,
          backgroundImage: 'none',
          '&:hover': {
            cursor: shouldShowFeedbackSummaryActions ? 'pointer' : undefined,
            borderColor: (theme) =>
              shouldShowFeedbackSummaryActions
                ? alpha(theme.palette.primary.main, 0.5)
                : undefined,
          },
        }}
        onClick={
          shouldShowFeedbackSummaryActions
            ? openFeedbackDetailsDrawer
            : undefined
        }
      >
        {shouldShowFeedbackSummary ? (
          <>
            <FeedbackSummary
              feedback={feedback}
              selectedLabels={selectedLabels}
              shouldShowFeedbackSummaryActions={
                shouldShowFeedbackSummaryActions
              }
            />
            <FeedbackDetailsConversation
              feedback={feedback}
              selectedLabels={selectedLabels}
              variant="preview"
              showOriginalLanguage={showUnTranslatedRecords}
            />
          </>
        ) : (
          feedbackSummaryPreview
        )}
      </Paper>
    </Tooltip>
  );
};
